import { type RouteRecordRaw } from '@bv/router';
import { deepMergeObjects } from '@bv/utils';
import { defineAsyncComponent } from 'vue';

const knowledgeBaseRoutes: RouteRecordRaw[] = [
  {
    path: '/ai/agents',
    redirect: { name: 'ai-agents-list' },
    meta: {
      permissions: 'read-ai-agents',
      breadcrumbs: [
        { label: 'ai.ai', route: { name: 'ai' } },
        { label: 'ai.nav.my_agents', route: { name: 'ai-agents-list' } },
      ],
    },
    children: [
      {
        path: '',
        children: [
          {
            path: '',
            name: 'ai-agents-list',
            component: () => import('@bvai/components/agent/AgentsList.vue'),
          },
          {
            path: 'add',
            name: 'ai-agents-add',
            component: () => import('@bvai/components/agent/form/AgentForm.vue'),
            meta: {
              permissions: 'write-ai-agents',
            },
          },
          {
            path: 'edit/:id',
            name: 'ai-agents-edit',
            component: () => import('@bvai/components/agent/form/AgentForm.vue'),
            meta: {
              permissions: 'update-ai-agents',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/ai/agents/knowledge-base',
    name: 'ai-agents-knowledge-base',
    meta: {
      permissions: 'read-ai-knowledge-base',
      breadcrumbs: [
        { label: 'ai.ai', route: { name: 'ai' } },
        { label: 'ai.nav.knowledge_base', route: { name: 'ai-agents-knowledge-base' } },
      ],
    },
    redirect: { name: 'ai-agents-knowledge-base-list' },
    children: [
      {
        path: '',
        name: 'ai-agents-knowledge-base-list',
        component: defineAsyncComponent(() => import('@bvai/components/knowledge-base/BvaiKnowledgeBaseList.vue')),
      },
      {
        path: ':knowledgeBaseId',
        name: 'ai-agents-knowledge-base-show',
        component: defineAsyncComponent(() => import('@bvai/components/knowledge-base/BvaiKnowledgeBaseShow.vue')),
      },
    ],
  },
];

function filterExcludedRoutes(routes: RouteRecordRaw[], excludeList?: BvaiRouteNames[]): RouteRecordRaw[] {
  if (!excludeList?.length) {
    return routes;
  }

  return routes.reduce<RouteRecordRaw[]>((acc, route) => {
    if (excludeList.includes(route.name as BvaiRouteNames)) {
      return acc;
    }

    if (route.children?.length) {
      const filteredChildren = filterExcludedRoutes(route.children, excludeList);
      if (filteredChildren.length) {
        acc.push({ ...route, children: filteredChildren });
      }
    } else {
      acc.push(route);
    }

    return acc;
  }, []);
}

/**
 * Generates AI routes by merging the provided service route with knowledge-base routes.
 *
 * @param serviceRoute - The base service route configuration to merge with
 * @param options - Optional configuration (e.g., routes to exclude)
 * @returns A new route configuration with knowledge-base routes merged as children
 *
 * @example
 * const baseRoute = { path: '/ai', name: 'ai', component: AiView }
 * const routes = aiRoutesGenerator(baseRoute, {
 *   exclude_routes: ['ai-agents-knowledge-base-show'],
 * })
 */
export const aiRoutesGenerator = (serviceRoute: RouteRecordRaw, options?: AiRoutesOptions): RouteRecordRaw => {
  const children = filterExcludedRoutes(knowledgeBaseRoutes, options?.exclude_routes);
  return deepMergeObjects(serviceRoute, { children });
};
